import React, {
  useState,
  useEffect,
  useLayoutEffect,
  KeyboardEvent,
  useRef,
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { flushSync } from 'react-dom';
import ReactMarkdown from 'react-markdown';

import { usePathname, useRouter } from 'next/navigation';

import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import remarkGfm from 'remark-gfm';

import { useUserInfo } from '@/context/UserInfoContext';
import { FEEDBACK_TRIGGERS, targetDoc, useFeedback } from '@/feedback';
import { beginAction, errorReason } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';
import { useVisibilityError } from '@/hooks/useVisibilityError';
import { createNewDoc, DOC_VISIBILITY, DocVisibility, NewDocRequestError } from '@/lib/api';
import { DisplayUserFollow, NewDocRequest, OfferPostType, PublicDocId } from '@/lib/api';
import { getSubmitShortcutLabel, isApplePlatform } from '@/lib/utils';

import { InviteManager } from './InviteManager';
import { Modal } from './Modal';
import { VisibilityIcon, VisibilitySelector } from './Visibility';

/** Legacy feedback alias, excluded from mention suggestions even when already followed. */
const FEEDBACK_USERNAME = 'feedback';
/** Matches a standalone feedback mention without treating email-like text as a mention. */
const FEEDBACK_MENTION_RE = /(?:^|[^\w.!#$%&'*+\/=?^`{|}~"-])@feedback\b/i;
const INVITE_SUGGESTION_INDEX = -1;

type SuggestionSelectionIndex = number | null;

interface MatchWidthModifierState {
  styles: {
    popper: {
      width?: string;
    };
  };
  rects: {
    reference: {
      width: number;
    };
  };
  elements: {
    popper: HTMLElement;
    reference: Element;
  };
}

const matchWidthPopperModifier = {
  name: 'matchWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }: { state: MatchWidthModifierState }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }: { state: MatchWidthModifierState }) => {
    state.elements.popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`;

    return () => {
      state.elements.popper.style.width = '';
    };
  },
};

const getFollowedUsersSignature = (followedUsers: DisplayUserFollow[] | undefined): string =>
  JSON.stringify((followedUsers || []).map((follow) => follow.username));

interface ComposeBoxProps {
  onClose: () => void;
  /** Supplies the topic when the new document should inherit one. */
  topic?: string;
  citationId?: PublicDocId | null;
  placeholder?: string;
  content?: string;
  prefill?: ComposeBoxPrefillIntent;
  onContentChange?: (content: string) => void;
  inheritedVisibility?: DocVisibility;
  className?: string;
  show_hints?: boolean;
  show_controls?: boolean;
  postType?: OfferPostType;
  minRows?: number;
  /**
   * Optional override for the Ctrl/Cmd+Enter submit shortcut. When provided
   * (e.g. by OmniSearchBox), the keyboard shortcut delegates submission to the
   * parent instead of ComposeBox's own document-post path, so the parent can
   * record its own action breadcrumb (e.g. post_question) and apply its own
   * visibility — matching what clicking the parent's submit button does.
   */
  onSubmit?: () => void;
}

/** Describes a one-shot draft initialization request applied once per key. */
export interface ComposeBoxPrefillIntent {
  /** Reapplying requires a new key so parents can issue distinct intents. */
  key: string;
  /** Provides the full prefilled draft text. */
  content: string;
  /** Sets the initial caret or selection anchor. */
  selectionStart: number;
  /** Sets the selection end, defaulting to a collapsed caret. */
  selectionEnd?: number;
  /** Opens the mention picker immediately after prefill when appropriate. */
  openMentionPicker?: boolean;
}

/** Appends content after one blank line without altering the existing draft. */
export function appendComposeDraft(currentDraft: string, appendedContent: string): string {
  if (currentDraft.length === 0) {
    return appendedContent;
  }

  const separator = currentDraft.endsWith('\n\n')
    ? ''
    : currentDraft.endsWith('\n')
      ? '\n'
      : '\n\n';
  return `${currentDraft}${separator}${appendedContent}`;
}

/** Lets parent components (e.g. OmniSearch) trigger submission or focus programmatically. */
export interface ComposeBoxHandle {
  submit: () => Promise<void>;
  focus: () => void;
}

export const ComposeBox = forwardRef<ComposeBoxHandle, ComposeBoxProps>((props, ref) => {
  ComposeBox.displayName = 'ComposeBox';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composeFieldAnchorRef = useRef<HTMLDivElement>(null);
  const suggestionRowIdPrefix = React.useId();
  /** Records the last one-shot prefill intent already applied to local draft state. */
  const appliedPrefillKeyRef = useRef<string | null>(null);
  /** Defers caret placement until React has committed the requested draft value. */
  const pendingPrefillCaretRef = useRef<ComposeBoxPrefillIntent | null>(null);
  /** Distinguishes user caret movement from selection events caused by our own placement. */
  const lastSelectionRef = useRef<{ start: number; end: number } | null>(null);
  /**
   * Keeps a prefill key eligible for one follow-up suggestion recompute after async user-info changes.
   *
   * Invariant: this is set only while an active prefill still owns the mention picker and is cleared as
   * soon as the user edits, inserts a mention, opens invite flow, or dismisses the picker.
   */
  const pendingPrefillSuggestionSyncKeyRef = useRef<string | null>(null);
  /**
   * Remembers that the user navigated suggestion rows for the active prefill key.
   *
   * Invariant: this never means "preserve everything"; it only preserves the current selection index
   * across late suggestion-list refreshes for that same prefill key while pending prefill sync remains
   * active.
   */
  const userNavigatedPrefillSelectionKeyRef = useRef<string | null>(null);
  const [content, setContent] = useState<string>(props.content || '');
  const hasContent = content.trim().length > 0;
  // Keep migration guidance independent of legacy feedback composition and mention suggestions.
  const showFeedbackHint = FEEDBACK_MENTION_RE.test(content);
  const feedbackLocation = props.citationId
    ? 'on the post you’re replying to'
    : 'in the search box menu';
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [showUserSuggestions, setShowUserSuggestions] = useState<boolean>(false);
  const [userSuggestions, setUserSuggestions] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<SuggestionSelectionIndex>(0);
  const [visibility, setVisibility] = useState<DocVisibility>(
    props.inheritedVisibility || DOC_VISIBILITY.Public
  );
  const visibilityError = useVisibilityError(content, visibility, props.inheritedVisibility);
  const canPost = hasContent && !isPosting && !visibilityError;
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invalidMentions, setInvalidMentions] = useState<string[]>([]);
  const [submitShortcutLabel, setSubmitShortcutLabel] = useState('Ctrl+Enter or Cmd+Enter');
  const [authExpiredError, setAuthExpiredError] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { userInfo } = useUserInfo();
  const hasSignedInUser = Boolean(userInfo);
  const { openFeedback } = useFeedback();
  const followedUsersSignature = getFollowedUsersSignature(userInfo?.followed_users);

  useEffect(() => {
    if (props.content !== undefined) {
      setContent(props.content);
    }
  }, [props.content]);

  /** Reports whether no suggestion row is currently selected. */
  const isNoSuggestionSelection = (
    selectionIndex: SuggestionSelectionIndex
  ): selectionIndex is null => selectionIndex === null;

  /** Reports whether the invite row is currently selected. */
  const isInviteSuggestionSelection = (
    selectionIndex: SuggestionSelectionIndex
  ): selectionIndex is typeof INVITE_SUGGESTION_INDEX => selectionIndex === INVITE_SUGGESTION_INDEX;

  /** Reports whether a concrete user-suggestion row is currently selected. */
  const isUserSuggestionSelection = (
    selectionIndex: SuggestionSelectionIndex
  ): selectionIndex is number => selectionIndex !== null && selectionIndex >= 0;

  /** Chooses the initial active row for a bare @ query. */
  const deriveEmptyAtSelectionIndex = (args: {
    suggestions: string[];
    includeInviteOption: boolean;
  }): SuggestionSelectionIndex => {
    if (args.includeInviteOption) {
      return INVITE_SUGGESTION_INDEX;
    }

    return args.suggestions.length > 0 ? 0 : null;
  };

  /** Maps a suggestion selection to its DOM row id. */
  const getSuggestionRowId = (index: SuggestionSelectionIndex): string | null => {
    if (isNoSuggestionSelection(index)) {
      return null;
    }

    return isInviteSuggestionSelection(index)
      ? `${suggestionRowIdPrefix}-user-suggestion-invite`
      : `${suggestionRowIdPrefix}-user-suggestion-${index}`;
  };

  /** Keeps the active suggestion row visible while navigating the picker. */
  const scrollSuggestionIntoView = (index: number | null) => {
    const rowId = getSuggestionRowId(index);
    if (!rowId) {
      return;
    }

    const element = document.getElementById(rowId);
    element?.scrollIntoView({ block: 'nearest' });
  };

  /** Computes the next picker selection for keyboard navigation. */
  const moveSuggestionSelection = (args: {
    previousIndex: SuggestionSelectionIndex;
    direction: 'up' | 'down';
    includeInviteOption: boolean;
    suggestionCount: number;
  }): SuggestionSelectionIndex => {
    const { previousIndex, direction, includeInviteOption, suggestionCount } = args;

    if (direction === 'down') {
      if (isNoSuggestionSelection(previousIndex)) {
        if (suggestionCount > 0) {
          return 0;
        }

        return includeInviteOption ? INVITE_SUGGESTION_INDEX : null;
      }

      if (isInviteSuggestionSelection(previousIndex)) {
        return suggestionCount > 0 ? 0 : INVITE_SUGGESTION_INDEX;
      }

      return Math.min(previousIndex + 1, suggestionCount - 1);
    }

    if (isNoSuggestionSelection(previousIndex)) {
      if (includeInviteOption) {
        return INVITE_SUGGESTION_INDEX;
      }

      return suggestionCount > 0 ? 0 : null;
    }

    if (isInviteSuggestionSelection(previousIndex)) {
      return INVITE_SUGGESTION_INDEX;
    }

    if (previousIndex === 0 && includeInviteOption) {
      return INVITE_SUGGESTION_INDEX;
    }

    return Math.max(previousIndex - 1, 0);
  };

  /** Preserves a user-chosen selection when suggestion data refreshes. */
  const deriveSelectionIndex = (args: {
    currentSelection: SuggestionSelectionIndex;
    suggestions: string[];
    includeInviteOption: boolean;
    query: string;
    preserveSelection: boolean;
  }): SuggestionSelectionIndex => {
    if (args.preserveSelection) {
      if (isInviteSuggestionSelection(args.currentSelection) && args.includeInviteOption) {
        return INVITE_SUGGESTION_INDEX;
      }

      if (
        isUserSuggestionSelection(args.currentSelection) &&
        args.currentSelection < args.suggestions.length
      ) {
        // TODO(3): Preserving by numeric index can select a different username if `followed_users`
        // reorder between refreshes. Future cleanup should preserve by username identity instead.
        return args.currentSelection;
      }

      if (isNoSuggestionSelection(args.currentSelection)) {
        return null;
      }
    }

    if (args.query.length === 0) {
      return deriveEmptyAtSelectionIndex({
        suggestions: args.suggestions,
        includeInviteOption: args.includeInviteOption,
      });
    }

    if (args.suggestions.length > 0) {
      return 0;
    }

    return args.includeInviteOption ? INVITE_SUGGESTION_INDEX : null;
  };

  /** Stops future late-prefill resync from overwriting active user actions. */
  const clearPendingPrefillTracking = () => {
    pendingPrefillSuggestionSyncKeyRef.current = null;
    userNavigatedPrefillSelectionKeyRef.current = null;
  };

  /**
   * Recomputes invalid mentions and @suggestions for a draft snapshot.
   *
   * TODO(2): Extract the prefill synchronization and mention-picker draft/suggestion derivation into a
   * dedicated hook or state machine so this component no longer interleaves one-shot prefill ownership,
   * async user-info refresh handling, and keyboard-selection preservation inside one render scope.
   */
  const syncDraftState = (
    nextContent: string,
    selectionStart: number,
    options?: {
      openMentionPicker?: boolean;
      preserveSelection?: boolean;
    }
  ) => {
    const openMentionPicker = options?.openMentionPicker ?? true;
    const preserveSelection = options?.preserveSelection ?? false;
    setContent(nextContent);
    setCursorPosition(selectionStart);

    const completedMentionRegex = /@(\w+)(?=\s|[.!?,:;])/g;
    const completedMentions = Array.from(nextContent.matchAll(completedMentionRegex), (m) => m[1]);
    const endMentionMatch = nextContent.match(/@(\w+)$/);
    if (endMentionMatch && selectionStart <= nextContent.length - endMentionMatch[0].length) {
      completedMentions.push(endMentionMatch[1]);
    }

    const validUsernames = userInfo?.followed_users?.map((follow) => follow.username) || [];
    const validMentions = new Set(validUsernames);
    const invalid = completedMentions.filter((mention) => !validMentions.has(mention));
    setInvalidMentions(invalid);

    if (!openMentionPicker) {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
      return;
    }

    const lastAtSymbolIndex = nextContent.lastIndexOf('@', selectionStart);
    if (lastAtSymbolIndex === -1 || selectionStart <= lastAtSymbolIndex) {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
      return;
    }

    const query = nextContent.slice(lastAtSymbolIndex + 1, selectionStart);
    if (/\s/.test(query)) {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
      return;
    }

    const queryLower = query.toLowerCase();
    const followedUserSuggestions =
      userInfo?.followed_users
        ?.filter(
          (follow: DisplayUserFollow) =>
            follow.username.toLowerCase() !== FEEDBACK_USERNAME &&
            follow.username.toLowerCase().startsWith(queryLower)
        )
        .map((follow: DisplayUserFollow) => follow.username) || [];
    const nextSuggestions = Array.from(new Set(followedUserSuggestions));
    const showInviteOption = query.length === 0 && hasSignedInUser;

    if (nextSuggestions.length > 0 || showInviteOption) {
      setUserSuggestions(nextSuggestions);
      setShowUserSuggestions(true);

      setSelectedSuggestionIndex(
        deriveSelectionIndex({
          currentSelection: selectedSuggestionIndex,
          suggestions: nextSuggestions,
          includeInviteOption: showInviteOption,
          query,
          preserveSelection,
        })
      );
    } else {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
    }
  };

  useLayoutEffect(() => {
    if (!props.prefill) {
      return;
    }

    const { content: nextContent, selectionStart, openMentionPicker = false } = props.prefill;

    const isNewPrefillKey = appliedPrefillKeyRef.current !== props.prefill.key;

    if (isNewPrefillKey) {
      /**
       * A new prefill key is a one-shot ownership transfer of the draft.
       *
       * Invariant: only a newly observed key may overwrite content/caret from props.prefill. Later
       * reruns for the same key may refresh suggestions from async user info, but must not reset user
       * edits or keyboard navigation state.
       */
      appliedPrefillKeyRef.current = props.prefill.key;
      pendingPrefillSuggestionSyncKeyRef.current = openMentionPicker ? props.prefill.key : null;
      userNavigatedPrefillSelectionKeyRef.current = null;
      pendingPrefillCaretRef.current = props.prefill;

      syncDraftState(nextContent, selectionStart, {
        openMentionPicker,
      });

      return;
    }

    /**
     * Same-key reruns are limited to late suggestion refreshes for the still-owned prefill.
     *
     * Invariant: if the picker is closed or pending prefill sync was cleared by user action, this effect
     * must become a no-op so followed-user updates cannot reopen or retarget suggestions.
     */
    if (!openMentionPicker || pendingPrefillSuggestionSyncKeyRef.current !== props.prefill.key) {
      return;
    }

    const textarea = textareaRef.current;
    const nextSelectionStart =
      textarea && document.activeElement === textarea
        ? (textarea.selectionStart ?? props.prefill.selectionStart)
        : props.prefill.selectionStart;
    syncDraftState(nextContent, nextSelectionStart, {
      openMentionPicker: true,
      preserveSelection: userNavigatedPrefillSelectionKeyRef.current === props.prefill.key,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this effect is intentionally keyed to prefill.key, not every props.prefill or syncDraftState change
  }, [props.prefill?.key, followedUsersSignature, hasSignedInUser]);

  // Layout effects finish inside a parent's flushSync, keeping focus in the opening tap gesture.
  useLayoutEffect(() => {
    const pending = pendingPrefillCaretRef.current;
    const textarea = textareaRef.current;
    if (!pending || !textarea || content !== pending.content) {
      return;
    }

    const end = pending.selectionEnd ?? pending.selectionStart;
    textarea.focus();
    textarea.setSelectionRange(pending.selectionStart, end);
    lastSelectionRef.current = { start: pending.selectionStart, end };
    pendingPrefillCaretRef.current = null;
  });

  useEffect(() => {
    setSubmitShortcutLabel(getSubmitShortcutLabel());
  }, []);

  /** Drives the live preview toggle — only shown when the draft has markdown syntax. */
  const containsMarkdown = (text: string): boolean => {
    const markdownRegex =
      /(\*\*|__|\*|_|`|#|\[.*\]\(.*\)|!\[.*\]\(.*\)|\n\s*[-*+]\s|\n\s*\d+\.\s|\n\s*>\s)/;
    return markdownRegex.test(text);
  };

  useImperativeHandle(ref, () => ({
    submit: handlePost,
    focus: () => textareaRef.current?.focus(),
  }));

  /**
   * Submits the compose box content as a new document.
   * On 401, shows login-and-return guidance while preserving the user's draft.
   * Other failures surface a generic inline error so posting problems are never silent.
   */
  const handlePost = async () => {
    if (!canPost) {
      return;
    }

    setAuthExpiredError(false);
    setPostError(null);
    setIsPosting(true);
    // Feedback breadcrumb: record the message-post attempt now; outcome is marked success/failure below.
    const action = beginAction(FEEDBACK_ACTION.POST_MESSAGE);
    try {
      const topic = props.topic;
      const requestBody: NewDocRequest = {
        content,
        topic,
        visibility,
      };

      if (props.postType) {
        requestBody.post_type = props.postType;
      }

      if (props.citationId) {
        requestBody.citation = props.citationId;
      }

      console.log('Posting content:', requestBody);

      const responseData = await createNewDoc(requestBody);
      console.log('New doc id:', responseData.doc_id);

      action.succeeded();
      props.onClose();
      router.push(`/doc/${responseData.doc_id}?processing_started=1`);
    } catch (error) {
      if (error instanceof NewDocRequestError && error.status === 401) {
        action.failed({ error: 'Session expired' });
        setAuthExpiredError(true);
        return;
      }
      action.failed({ error: errorReason(error) });
      console.error('Error posting content:', error);
      setPostError("We couldn't post your message. Your draft is still here. Please try again.");
    } finally {
      setIsPosting(false);
    }
  };

  const loginHref = `/login?redirect=${encodeURIComponent(pathname || '/')}`;

  const showPreview = containsMarkdown(content);

  /** Also tracks @mention state and suggestion visibility alongside draft sync. */
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newContent = e.target.value;
    const selectionStart = e.target.selectionStart || 0;
    lastSelectionRef.current = {
      start: selectionStart,
      end: e.target.selectionEnd ?? selectionStart,
    };
    clearPendingPrefillTracking();
    syncDraftState(newContent, selectionStart, {
      openMentionPicker: true,
    });

    if (props.onContentChange) {
      props.onContentChange(newContent);
    }
  };

  /** Keeps the picker tied to a collapsed caret inside the current mention query. */
  const handleTextareaSelect = () => {
    const textarea = textareaRef.current;
    if (!textarea || pendingPrefillCaretRef.current || document.activeElement !== textarea) {
      return;
    }

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? start;
    if (lastSelectionRef.current?.start === start && lastSelectionRef.current.end === end) {
      return;
    }

    lastSelectionRef.current = { start, end };
    clearPendingPrefillTracking();
    syncDraftState(textarea.value, start, { openMentionPicker: start === end });
  };

  /** Opens feedback for the current reply target without changing the compose draft. */
  const handleOpenFeedback = () => {
    clearPendingPrefillTracking();
    setShowUserSuggestions(false);
    openFeedback({
      trigger: props.citationId ? FEEDBACK_TRIGGERS.FLAG : FEEDBACK_TRIGGERS.MENU,
      target: props.citationId ? targetDoc(props.citationId) : undefined,
    });
  };

  /**
   * Multiplexes keyboard input: arrow/enter/tab/escape navigate the @mention
   * dropdown when it's open, otherwise Ctrl+Enter (Cmd+Enter on Mac) submits.
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (showUserSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (props.prefill && appliedPrefillKeyRef.current === props.prefill.key) {
          userNavigatedPrefillSelectionKeyRef.current = props.prefill.key;
        }
        setSelectedSuggestionIndex((prevIndex) => {
          const newIndex = moveSuggestionSelection({
            previousIndex: prevIndex,
            direction: 'down',
            includeInviteOption: hasSignedInUser,
            suggestionCount: userSuggestions.length,
          });
          scrollSuggestionIntoView(newIndex);
          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (props.prefill && appliedPrefillKeyRef.current === props.prefill.key) {
          userNavigatedPrefillSelectionKeyRef.current = props.prefill.key;
        }
        setSelectedSuggestionIndex((prevIndex) => {
          const newIndex = moveSuggestionSelection({
            previousIndex: prevIndex,
            direction: 'up',
            includeInviteOption: hasSignedInUser,
            suggestionCount: userSuggestions.length,
          });
          scrollSuggestionIntoView(newIndex);
          return newIndex;
        });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (isNoSuggestionSelection(selectedSuggestionIndex)) {
          setShowUserSuggestions(false);
          return;
        }

        e.preventDefault();
        if (isInviteSuggestionSelection(selectedSuggestionIndex) && userInfo) {
          clearPendingPrefillTracking();
          setShowInviteModal(true);
          setShowUserSuggestions(false);
        } else if (
          isUserSuggestionSelection(selectedSuggestionIndex) &&
          selectedSuggestionIndex < userSuggestions.length
        ) {
          insertUsername(userSuggestions[selectedSuggestionIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearPendingPrefillTracking();
        setShowUserSuggestions(false);
      }
    } else if (e.key === 'Enter' && (isApplePlatform() ? e.metaKey : e.ctrlKey)) {
      e.preventDefault();
      // When a parent owns submission (e.g. OmniSearchBox), delegate so its own
      // traced submit and visibility are used instead of ComposeBox's post path.
      if (props.onSubmit) {
        props.onSubmit();
        return;
      }
      if (!canPost) {
        return;
      }

      handlePost();
    }
  };

  /** Splices the selected username into the draft at the active @-query position. */
  const insertUsername = (username: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursor = textarea.selectionStart ?? cursorPosition;
    const lastAtSymbolIndex = content.lastIndexOf('@', cursor);
    // Validate the live caret as well as the displayed suggestions before replacing any text.
    if (
      lastAtSymbolIndex === -1 ||
      cursor <= lastAtSymbolIndex ||
      textarea.selectionEnd !== cursor ||
      /\s/.test(content.slice(lastAtSymbolIndex + 1, cursor))
    )
      return;

    const beforeAt = content.slice(0, lastAtSymbolIndex);
    const afterCursor = content.slice(cursor);
    const needsTrailingSpace = afterCursor.length === 0 || !/^\s/.test(afterCursor);
    const insertion = `@${username}${needsTrailingSpace ? ' ' : ''}`;
    const newContent = `${beforeAt}${insertion}${afterCursor}`;
    clearPendingPrefillTracking();
    const newCursorPosition = lastAtSymbolIndex + insertion.length;
    // Commit the value before placing the caret, while the suggestion tap can still open a keyboard.
    flushSync(() => {
      setContent(newContent);
      props.onContentChange?.(newContent);
      setCursorPosition(newCursorPosition);
      setShowUserSuggestions(false);
    });
    textarea.focus();
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    lastSelectionRef.current = { start: newCursorPosition, end: newCursorPosition };
  };

  // Prefills own their caret placement; other composers focus as soon as their input is mounted.
  useLayoutEffect(() => {
    if (!props.prefill) textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the mount transfers focus
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Guide legacy @feedback mentions to the form for the current compose reply target. */}
      {showFeedbackHint && (
        <Alert severity="info" role="status" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {'Use our new '}
            <Button
              type="button"
              size="small"
              startIcon={<FeedbackOutlinedIcon />}
              onClick={handleOpenFeedback}
              sx={{ verticalAlign: 'baseline', textTransform: 'none' }}
            >
              Give feedback
            </Button>
            {` form to share your thoughts with the team. Also available ${feedbackLocation}.`}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            We no longer monitor posts tagged <code>@feedback</code>, and the tag no longer makes
            posts private. Your selected visibility (<strong>{visibility}</strong>) applies.
          </Typography>
        </Alert>
      )}

      {/* Invalid mentions warning */}
      {invalidMentions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Warning: You mentioned @{invalidMentions.join(', @')} but you don&apos;t follow{' '}
            {invalidMentions.length > 1 ? 'these users' : 'this user'}. Only people you follow will
            be notified.
          </Typography>
        </Alert>
      )}

      {postError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">{postError}</Typography>
        </Alert>
      )}

      {authExpiredError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              component="a"
              href={loginHref}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              Log in
            </Button>
          }
        >
          <Typography variant="body2">
            Your session expired. Your draft is still here — log in, then return to this tab and
            click Post again.
          </Typography>
        </Alert>
      )}

      {/* Main compose area */}
      <Box sx={{ position: 'relative', overflow: 'visible' }}>
        <Box ref={composeFieldAnchorRef} sx={{ position: 'relative' }}>
          <TextField
            inputRef={textareaRef}
            multiline
            fullWidth
            minRows={props.minRows || 3}
            maxRows={20}
            placeholder={props.placeholder || 'What are you thinking?'}
            value={content}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onSelect={handleTextareaSelect}
            disabled={isPosting}
            slotProps={{
              htmlInput: {
                'aria-label': props.placeholder || (props.citationId ? 'Reply' : 'New post'),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                lineHeight: 1.5,
              },
            }}
          />

          {/* User suggestions dropdown */}
          {showUserSuggestions && (
            <Popper
              open
              anchorEl={composeFieldAnchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: 1400 }}
              popperOptions={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                  {
                    name: 'flip',
                    enabled: true,
                  },
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    options: {
                      padding: 8,
                    },
                  },
                  matchWidthPopperModifier,
                ],
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  backgroundColor: 'var(--mui-palette-background-paper)',
                  opacity: 1,
                }}
              >
                <List dense>
                  {userInfo && (
                    <ListItemButton
                      id={getSuggestionRowId(INVITE_SUGGESTION_INDEX) ?? undefined}
                      selected={isInviteSuggestionSelection(selectedSuggestionIndex)}
                      onClick={() => {
                        clearPendingPrefillTracking();
                        setShowInviteModal(true);
                        setShowUserSuggestions(false);
                      }}
                    >
                      <ListItemIcon>
                        <PersonAddIcon />
                      </ListItemIcon>
                      <ListItemText primary="Invite someone new..." />
                    </ListItemButton>
                  )}
                  {userSuggestions.map((username, index) => (
                    <ListItemButton
                      key={`${username}-${index}`}
                      id={getSuggestionRowId(index) ?? undefined}
                      selected={selectedSuggestionIndex === index}
                      // Keep desktop pointer clicks from blurring the editor; touch scrolling stays native.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => insertUsername(username)}
                    >
                      <ListItemIcon />
                      <ListItemText primary={`@${username}`} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Popper>
          )}
        </Box>

        {/* Markdown preview */}
        {showPreview && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Preview:
              </Typography>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                {/* Using ReactMarkdown for live preview - instant feedback for users while typing.
                      Final document rendering uses backend-parsed structured content for consistency. */}
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </Box>
            </Box>
          </>
        )}

        {/* Help text when typing */}
        {content.length > 0 && props.show_controls !== false && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Markdown is supported. Press {submitShortcutLabel} to post. Enter inserts a new line.
          </Typography>
        )}

        {/* Controls */}
        {props.show_controls !== false && (
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilitySelector
                visibility={visibility}
                setVisibility={setVisibility}
                inheritedVisibility={props.inheritedVisibility}
                showDropdown={showVisibilityDropdown}
                setShowDropdown={setShowVisibilityDropdown}
                position="above"
              />
              {visibilityError && (
                <Alert severity="error" sx={{ py: 0, px: 1 }}>
                  <Typography variant="caption">{visibilityError}</Typography>
                </Alert>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="text" onClick={props.onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handlePost}
                disabled={!canPost}
                title={`Post (${submitShortcutLabel})`}
              >
                {isPosting ? 'Posting...' : 'Post'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Hints */}
      {props.show_hints && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Markdown supported:</strong> **bold**, *italic*, `code`, [links](url), etc.
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            <strong>@mentions:</strong> Type @ to mention followed users or invite new ones
          </Typography>
        </Box>
      )}

      {/* Invite modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite someone to SureShop"
      >
        {/* TODO(2): ENG-965 — ComposeBox still does not offer its own copy/share flow or automatic
            compose-box insertion after invite creation because the backend response does not include the
            invited user's eventual username. */}
        <InviteManager
          threadContext={props.citationId ?? undefined}
          onDone={() => setShowInviteModal(false)}
        />
      </Modal>
    </Box>
  );
});

ComposeBox.displayName = 'ComposeBox';
